import React from 'react';
import {
  ChevronRight,
  Coins,
  History,
  PencilLine,
  UserRound,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { BRAND_TIFFANY_BLUE } from '../../constants/branding';
import api from '../../lib/api';
import { fetchCurrentCustomerSession } from '../../utils/auth/customerSession';
import { saveCustomerAuthToken } from '../../utils/auth/customerAuthToken';
import SubpageHeader from './SubpageHeader';
import { useReserveContext } from '../../contexts/useReserveContext';
import { useI18n } from '../../i18n';

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { storeId } = useReserveContext();
  const { t } = useI18n();
  const [customerName, setCustomerName] = React.useState(localStorage.getItem('customerName') || '');
  const [customerId, setCustomerId] = React.useState(localStorage.getItem('customerId') || '');
  const [pointBalance, setPointBalance] = React.useState<number | null>(null);
  const [pointsVisible, setPointsVisible] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    const loadSession = async () => {
      try {
        const session = await fetchCurrentCustomerSession();
        if (!active) return;

        const nextId = String(session.customer_id);
        localStorage.setItem('customerId', nextId);
        localStorage.setItem('customerName', session.name);
        saveCustomerAuthToken(session.auth_token);
        setCustomerId(nextId);
        setCustomerName(session.name);
      } catch (error) {
        if (!active) return;
        console.error('Failed to load customer session in MyPage:', error);
      }
    };

    void loadSession();

    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    let active = true;

    const loadPoints = async () => {
      try {
        const res = await api.get<{ visible?: boolean; total_balance_points: number }>('/api/customer-app/points', {
          params: storeId ? { store_id: storeId } : undefined,
        });
        if (!active) return;
        const visible = Boolean(res.data.visible);
        setPointsVisible(visible);
        setPointBalance(visible ? Number(res.data.total_balance_points ?? 0) : null);
      } catch (error) {
        if (!active) return;
        console.error('Failed to load customer points in MyPage:', error);
        setPointsVisible(false);
        setPointBalance(null);
      }
    };

    void loadPoints();

    return () => {
      active = false;
    };
  }, [storeId]);

  const menuItems = [
    {
      title: t('mypage.personalInfo'),
      description: t('mypage.personalInfoDetail'),
      icon: PencilLine,
      onClick: () => navigate('/reserve/profile'),
    },
    ...(pointsVisible
      ? [{
          title: t('mypage.points'),
          description: t('mypage.pointsDetail'),
          icon: Coins,
          onClick: () => navigate('/reserve/points'),
        }]
      : []),
    {
      title: t('mypage.visitHistory'),
      description: t('mypage.visitHistoryDetail'),
      icon: History,
      onClick: () => navigate('/reserve/history'),
    },
  ];

  return (
    <div className="space-y-4">
      <SubpageHeader title={t('mypage.title')} onBack={() => navigate('/reserve')} />

      <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <div
          className="px-5 py-5 text-white"
          style={{
            background: `linear-gradient(135deg, ${BRAND_TIFFANY_BLUE}, #089d99)`,
          }}
        >
          <div className="flex items-start gap-3">
            <div className="rounded-[20px] bg-white/15 p-3">
              <UserRound className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/65">
                {t('account.member')}
              </div>
              <div className="mt-1 truncate text-[26px] font-semibold tracking-[-0.03em]">
                {customerName || t('account.guest')}
              </div>
              <div className="mt-1 text-sm text-white/75">
                {t('mypage.accountCopy')}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 px-5 py-4">
          {customerId ? (
            <div className="rounded-full bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600">
              ID {customerId}
            </div>
          ) : null}
          <div className="rounded-full bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
            {t('settings.activeAccount')}
          </div>
          {pointBalance != null ? (
            <div className="rounded-full bg-cyan-50 px-3 py-2 text-sm font-medium text-cyan-700">
              {pointBalance.toLocaleString()} pt
            </div>
          ) : null}
        </div>
      </section>

      <section className="space-y-3">
        {menuItems.map(({ title, description, icon: Icon, onClick }) => (
          <button
            key={title}
            type="button"
            onClick={onClick}
            className="flex w-full items-center justify-between gap-3 rounded-[28px] border border-slate-200 bg-white px-4 py-4 text-left shadow-[0_18px_42px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_48px_rgba(15,23,42,0.12)]"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-[18px] bg-slate-100 p-3 text-slate-900">
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-semibold text-slate-900">{title}</div>
                <div className="mt-1 text-sm text-slate-500">{description}</div>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
          </button>
        ))}
      </section>
    </div>
  );
};

export default MyPage;
